/**
 * Filter panel for transport mode toggles.
 */

import { useCallback } from 'react';
import { useFilterStore, ALL_TRANSPORT_MODES } from '../../stores/filterStore';
import type { TransportMode } from '../../types/api';

// Transport mode display config
const MODE_CONFIG: Record<TransportMode, { label: string; icon: string; color: string }> = {
  tram: { label: 'Tram', icon: '🚋', color: '#0077be' },
  bus: { label: 'Bus', icon: '🚌', color: '#ff6b35' },
  ferry: { label: 'Ferry', icon: '⛴️', color: '#00a86b' },
  train: { label: 'Train', icon: '🚆', color: '#6c757d' },
  taxi: { label: 'Taxi', icon: '🚕', color: '#ffc107' },
  teletaxi: { label: 'Flex', icon: '🚐', color: '#9c27b0' },
};

export interface FilterPanelProps {
  /** Additional CSS class */
  className?: string;
}

export function FilterPanel({ className = '' }: FilterPanelProps) {
  const enabledModes = useFilterStore((state) => state.enabledModes);
  const toggleMode = useFilterStore((state) => state.toggleMode);
  const enableAllModes = useFilterStore((state) => state.enableAllModes);
  const disableAllModes = useFilterStore((state) => state.disableAllModes);

  const handleToggle = useCallback(
    (mode: TransportMode) => {
      toggleMode(mode);
    },
    [toggleMode]
  );

  const allEnabled = ALL_TRANSPORT_MODES.every((mode) => enabledModes.has(mode));
  const noneEnabled = enabledModes.size === 0;

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 ${className}`}
      role="group"
      aria-label="Transport mode filters"
    >
      {/* Quick actions */}
      <div className="flex gap-2 mb-3 text-sm">
        <button
          onClick={enableAllModes}
          disabled={allEnabled}
          className="px-2 py-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          All
        </button>
        <button
          onClick={disableAllModes}
          disabled={noneEnabled}
          className="px-2 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          None
        </button>
      </div>

      {/* Mode toggles */}
      <div className="flex flex-wrap gap-2">
        {ALL_TRANSPORT_MODES.map((mode) => {
          const config = MODE_CONFIG[mode];
          const isEnabled = enabledModes.has(mode);

          return (
            <button
              key={mode}
              onClick={() => handleToggle(mode)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                transition-colors duration-150
                ${
                  isEnabled
                    ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }
              `}
              aria-pressed={isEnabled}
              aria-label={`${config.label} ${isEnabled ? 'enabled' : 'disabled'}`}
            >
              <span aria-hidden="true">{config.icon}</span>
              <span>{config.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
